import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('FeatureFlagService', 'features');

export interface FeatureFlags {
  dualPhotoEnabled: boolean;
  showMigrationPrompt: boolean;
  autoCropEnabled: boolean;
  percentRollout: number;
}

export const OB_COMPENSATION_FEATURE_FLAG = 'ob_compensation_feature';

/** Shape of the feature_flags.metadata JSONB consumed by buildFlags. */
interface FlagMetadata {
  show_migration_prompt?: boolean;
  auto_crop_enabled?: boolean;
  rollout_percentage?: number;
}

/**
 * Service for managing feature flags and progressive rollout
 */
class FeatureFlagService {
  private cache: Map<string, { flags: FeatureFlags; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get feature flags for a specific user
   */
  async getFlags(userId: string): Promise<FeatureFlags> {
    try {
      // Check cache first
      const cached = this.cache.get(userId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.flags;
      }

      // Check for user-specific override
      const { data: userOverride } = await supabase
        .from('feature_flags_users')
        .select('*')
        .eq('user_id', userId)
        .eq('flag_name', 'dual_photos')  // Use flag_name
        .single();

      if (userOverride?.enabled !== undefined) {
        const flags = this.buildFlags(userOverride.enabled, userOverride.metadata);
        this.cache.set(userId, { flags, timestamp: Date.now() });
        return flags;
      }

      // Get global feature flag configuration
      const { data: globalFlag } = await supabase
        .from('feature_flags')
        .select('*')
        .eq('flag_name', 'dual_photos')  // Use flag_name
        .single();

      if (!globalFlag) {
        // Feature flag doesn't exist, return defaults
        const flags = this.buildFlags(false);
        this.cache.set(userId, { flags, timestamp: Date.now() });
        return flags;
      }

      // Check if globally enabled
      if (!globalFlag.enabled) {
        const flags = this.buildFlags(false, globalFlag.metadata);
        this.cache.set(userId, { flags, timestamp: Date.now() });
        return flags;
      }

      // Check percentage rollout
      const isInRollout = this.checkPercentageRollout(userId, globalFlag.rollout_percentage);
      const flags = this.buildFlags(isInRollout, globalFlag.metadata);
      this.cache.set(userId, { flags, timestamp: Date.now() });
      
      return flags;
    } catch (error) {
      log.error('Error getting feature flags', { userId }, error as Error);
      // Return safe defaults on error
      return this.buildFlags(false);
    }
  }

  /**
   * Set user-specific feature override
   */
  async setUserOverride(
    userId: string, 
    enabled: boolean, 
    metadata?: FlagMetadata
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('feature_flags_users')
        .upsert({
          flag_name: 'dual_photos',  // Use flag_name
          user_id: userId,
          enabled,
          metadata: metadata || {}
        }, {
          onConflict: 'flag_name,user_id'
        });

      if (error) {
throw error;
}

      // Clear cache for this user
      this.cache.delete(userId);
      
      log.info('User feature override set', { userId, enabled });
    } catch (error) {
      log.error('Error setting user override', { userId, enabled }, error as Error);
      throw error;
    }
  }

  /**
   * Remove user-specific feature override
   */
  async removeUserOverride(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('feature_flags_users')
        .delete()
        .eq('flag_name', 'dual_photos')  // Use flag_name
        .eq('user_id', userId);

      if (error) {
throw error;
}

      // Clear cache for this user
      this.cache.delete(userId);
      
      log.info('User feature override removed', { userId });
    } catch (error) {
      log.error('Error removing user override', { userId }, error as Error);
      throw error;
    }
  }

  /**
   * Update global feature flag settings (admin only)
   */
  async updateGlobalFlag(
    enabled: boolean,
    rolloutPercentage: number = 0,
    metadata?: FlagMetadata
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('feature_flags')
        .upsert({
          flag_name: 'dual_photos',  // Use flag_name
          enabled,
          rollout_percentage: rolloutPercentage,
          metadata: metadata || {},  // Use metadata column
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'flag_name'
        });

      if (error) {
throw error;
}

      // Clear all cache entries
      this.cache.clear();
      
      log.info('Global feature flag updated', { 
        enabled, 
        rolloutPercentage 
      });
    } catch (error) {
      log.error('Error updating global flag', { 
        enabled, 
        rolloutPercentage 
      }, error as Error);
      throw error;
    }
  }

  /**
   * Get rollout statistics
   */
  async getRolloutStats(): Promise<{
    totalUsers: number;
    enabledUsers: number;
    overrideUsers: number;
    rolloutPercentage: number;
  }> {
    try {
      // Get total user count
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // Get users with overrides
      const { count: overrideUsers } = await supabase
        .from('feature_flags_users')
        .select('*', { count: 'exact', head: true })
        .eq('flag_name', 'dual_photos');  // Use flag_name

      // Get global rollout percentage
      const { data: globalFlag } = await supabase
        .from('feature_flags')
        .select('rollout_percentage, enabled')
        .eq('flag_name', 'dual_photos')  // Use flag_name
        .single();

      const rolloutPercentage = globalFlag?.rollout_percentage || 0;
      const globalEnabled = globalFlag?.enabled || false;

      // Calculate estimated enabled users
      const enabledByRollout = globalEnabled 
        ? Math.floor((totalUsers || 0) * (rolloutPercentage / 100))
        : 0;
      
      const { count: enabledOverrides } = await supabase
        .from('feature_flags_users')
        .select('*', { count: 'exact', head: true })
        .eq('flag_name', 'dual_photos')  // Use flag_name
        .eq('enabled', true);

      return {
        totalUsers: totalUsers || 0,
        enabledUsers: enabledByRollout + (enabledOverrides || 0),
        overrideUsers: overrideUsers || 0,
        rolloutPercentage
      };
    } catch (error) {
      log.error('Error getting rollout stats', {}, error as Error);
      throw error;
    }
  }

  /**
   * Clear cache for all users
   */
  clearCache(): void {
    this.cache.clear();
    log.info('Feature flag cache cleared');
  }

  // Private helper methods

  private buildFlags(
    enabled: boolean, 
    metadata?: FlagMetadata
  ): FeatureFlags {
    return {
      dualPhotoEnabled: enabled,
      showMigrationPrompt: enabled && (metadata?.show_migration_prompt ?? false),
      autoCropEnabled: enabled && (metadata?.auto_crop_enabled ?? false),
      percentRollout: metadata?.rollout_percentage ?? 0
    };
  }

  private checkPercentageRollout(
    userId: string, 
    percentage: number
  ): boolean {
    if (percentage >= 100) {
return true;
}
    if (percentage <= 0) {
return false;
}

    // Use consistent hash of user ID for deterministic rollout
    const hash = this.hashUserId(userId);
    return hash < percentage;
  }

  private hashUserId(userId: string): number {
    // Simple hash function that returns a number between 0-99
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash) % 100;
  }

  // ─── Generic flag-by-name reads ─────────────────────────────────────────
  // The methods above are tied to the legacy 'dual_photos' flag. New flags should use the
  // generic helpers below, which read by flag_name from the same feature_flags / feature_flags_users
  // tables and apply the same 5-min cache TTL keyed by (userId, flagName).

  private genericCache: Map<string, { enabled: boolean; timestamp: number }> = new Map();

  /**
   * Returns whether the named flag is enabled for the given user. Honors per-user override
   * (feature_flags_users), falls back to the global feature_flags row. Returns false if the
   * flag does not exist.
   */
  async isFlagEnabled(flagName: string, userId?: string): Promise<boolean> {
    const cacheKey = `${flagName}::${userId ?? '__global__'}`;
    const cached = this.genericCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.enabled;
    }

    try {
      if (userId) {
        const { data: override } = await supabase
          .from('feature_flags_users')
          .select('enabled')
          .eq('user_id', userId)
          .eq('flag_name', flagName)
          .maybeSingle();
        if (override && typeof override.enabled === 'boolean') {
          this.genericCache.set(cacheKey, { enabled: override.enabled, timestamp: Date.now() });
          return override.enabled;
        }
      }
      const { data: globalFlag } = await supabase
        .from('feature_flags')
        .select('enabled')
        .eq('flag_name', flagName)
        .maybeSingle();
      const enabled = Boolean(globalFlag?.enabled);
      this.genericCache.set(cacheKey, { enabled, timestamp: Date.now() });
      return enabled;
    } catch (e) {
      log.error('isFlagEnabled lookup failed', { flagName, userId }, e as Error);
      return false;
    }
  }

  /** Convenience for the OB compensation rollout. */
  async isObCompensationFeatureEnabled(userId?: string): Promise<boolean> {
    return this.isFlagEnabled(OB_COMPENSATION_FEATURE_FLAG, userId);
  }

  /** Force a refetch on next call (use after admin flips a flag). */
  invalidateGenericCache(flagName?: string): void {
    if (!flagName) {
      this.genericCache.clear();
      return;
    }
    for (const key of Array.from(this.genericCache.keys())) {
      if (key.startsWith(`${flagName}::`)) {
        this.genericCache.delete(key);
      }
    }
  }
}

// Export singleton instance
export const featureFlagService = new FeatureFlagService();

// Also export the class for testing
export { FeatureFlagService };